import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { usePixelSvgCanvas, type ToolKind } from "./PixelSvgCanvasContext";
import {
    EMPTY_CELL,
    buildSvgDocument,
    bresenhamLine,
    countFilledCells,
    fillEllipse,
    fillRect,
    parseHexColor,
    renderPixels,
    stampBrush,
    type CellShape,
    type OutputMode,
} from "../../utils/pixelSvgRender";
import { useUndoRedoKeyboard } from "../../hooks/useUndoRedoKeyboard";
import "./PixelSvgCanvas.scss";

const RAW_OUTPUT_AUTO_LIMIT = 50_000;
/** Visible canvas size in CSS pixels (square). */
const DISPLAY_SIZE = 480;
const PREVIEW_ZOOM_MIN = 0.25;
const PREVIEW_ZOOM_MAX = 8;

type DragState =
    | null
    | { kind: "freehand"; tool: "pencil" | "eraser"; lastX: number; lastY: number }
    | { kind: "shape"; tool: "rect" | "ellipse" | "line"; startX: number; startY: number; curX: number; curY: number };

export function PixelSvgCanvasViewer() {
    const {
        width,
        height,
        pixelsRef,
        pixelsVersion,
        tool,
        setTool,
        brushSize,
        color,
        outputMode,
        outputSize,
        cellShape,
        paintCell,
        paintStroke,
        eraseCell,
        eraseStroke,
        fillAt,
        commitRect,
        commitEllipse,
        commitLine,
        sampleColor,
        restoreLastPaintTool,
        setColor,
        snapshot,
        commit,
        undo,
        redo,
        showOutputPane,
        downloadSvg,
        editorWhiteBackground,
    } = usePixelSvgCanvas();

    const editorCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const dragStateRef = useRef<DragState>(null);
    const [hoverCell, setHoverCell] = useState<{ x: number; y: number } | null>(null);
    const [forceOutputRender, setForceOutputRender] = useState(0);
    const [previewZoom, setPreviewZoom] = useState(1);

    const zoomIn = () => {
        setPreviewZoom((z) =>
            Math.min(
                PREVIEW_ZOOM_MAX,
                Math.round(z * 1.25 * 100) / 100,
            ),
        );
    };
    const zoomOut = () => {
        setPreviewZoom((z) =>
            Math.max(
                PREVIEW_ZOOM_MIN,
                Math.round((z / 1.25) * 100) / 100,
            ),
        );
    };
    const resetPreviewZoom = () => setPreviewZoom(1);

    const stageW = DISPLAY_SIZE * previewZoom;
    const stageH = DISPLAY_SIZE * previewZoom;

    const filledCount = useMemo(
        () => countFilledCells(pixelsRef.current),
        // pixelsVersion is the dependency; pixelsRef.current is mutated in place
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pixelsVersion, width, height],
    );

    /** Blit pixel buffer to the editor canvas. */
    const blit = useCallback(() => {
        const cv = editorCanvasRef.current;
        if (!cv) return;
        const ctx = cv.getContext("2d");
        if (!ctx) return;
        if (cv.width !== width || cv.height !== height) {
            cv.width = width;
            cv.height = height;
        }
        const img = ctx.createImageData(width, height);
        const data = img.data;
        const px = pixelsRef.current;
        for (let i = 0, j = 0; i < px.length; i++, j += 4) {
            const v = px[i];
            if (v === EMPTY_CELL) {
                data[j] = 0;
                data[j + 1] = 0;
                data[j + 2] = 0;
                data[j + 3] = 0;
            } else {
                data[j] = (v >>> 16) & 0xff;
                data[j + 1] = (v >>> 8) & 0xff;
                data[j + 2] = v & 0xff;
                data[j + 3] = 255;
            }
        }
        ctx.putImageData(img, 0, 0);
    }, [width, height, pixelsRef]);

    useEffect(() => {
        blit();
    }, [blit, pixelsVersion]);

    /** Convert a pointer event into an integer cell coordinate. */
    const eventToCell = useCallback(
        (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
            const cv = editorCanvasRef.current;
            if (!cv) return { x: 0, y: 0 };
            const rect = cv.getBoundingClientRect();
            const px = e.clientX - rect.left;
            const py = e.clientY - rect.top;
            const cx = Math.floor((px / rect.width) * width);
            const cy = Math.floor((py / rect.height) * height);
            return {
                x: Math.max(0, Math.min(width - 1, cx)),
                y: Math.max(0, Math.min(height - 1, cy)),
            };
        },
        [width, height],
    );

    /** Draw the shape preview / brush outline onto the overlay canvas. */
    const renderOverlay = useCallback(() => {
        const cv = overlayCanvasRef.current;
        if (!cv) return;
        if (cv.width !== width || cv.height !== height) {
            cv.width = width;
            cv.height = height;
        }
        const ctx = cv.getContext("2d");
        if (!ctx) return;
        ctx.clearRect(0, 0, width, height);

        const drag = dragStateRef.current;
        if (drag && drag.kind === "shape") {
            const tmp = new Uint32Array(width * height);
            const packed = parseHexColor(color);
            if (drag.tool === "rect") {
                fillRect(tmp, width, height, drag.startX, drag.startY, drag.curX, drag.curY, packed);
            } else if (drag.tool === "ellipse") {
                fillEllipse(tmp, width, height, drag.startX, drag.startY, drag.curX, drag.curY, packed);
            } else {
                bresenhamLine(drag.startX, drag.startY, drag.curX, drag.curY, (x, y) => {
                    stampBrush(tmp, width, height, x, y, brushSize, packed);
                });
            }
            const img = ctx.createImageData(width, height);
            const data = img.data;
            for (let i = 0, j = 0; i < tmp.length; i++, j += 4) {
                const v = tmp[i];
                if (v === EMPTY_CELL) continue;
                data[j] = (v >>> 16) & 0xff;
                data[j + 1] = (v >>> 8) & 0xff;
                data[j + 2] = v & 0xff;
                data[j + 3] = 200;
            }
            ctx.putImageData(img, 0, 0);
        }

        if (hoverCell && (tool === "pencil" || tool === "eraser" || tool === "line")) {
            const half = Math.floor(brushSize / 2);
            const x0 = hoverCell.x - half;
            const y0 = hoverCell.y - half;
            ctx.save();
            ctx.lineWidth = 1 / Math.max(width, height);
            ctx.strokeStyle = "rgba(0, 0, 0, 0.85)";
            ctx.strokeRect(
                x0 + 0.05,
                y0 + 0.05,
                brushSize - 0.1,
                brushSize - 0.1,
            );
            ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
            ctx.strokeRect(
                x0 + 0.15,
                y0 + 0.15,
                brushSize - 0.3,
                brushSize - 0.3,
            );
            ctx.restore();
        }
    }, [width, height, brushSize, color, tool, hoverCell]);

    useEffect(() => {
        renderOverlay();
    }, [renderOverlay, pixelsVersion]);

    const onPointerDown = useCallback(
        (e: React.PointerEvent<HTMLCanvasElement>) => {
            if (e.button !== 0) return;
            const cv = editorCanvasRef.current;
            if (!cv) return;
            cv.setPointerCapture(e.pointerId);
            const cell = eventToCell(e);

            if (tool === "eyedropper") {
                const sampled = sampleColor(cell.x, cell.y);
                if (sampled) {
                    setColor(sampled);
                }
                restoreLastPaintTool();
                return;
            }

            if (tool === "bucket") {
                snapshot();
                fillAt(cell.x, cell.y);
                commit();
                return;
            }

            if (tool === "pencil" || tool === "eraser") {
                snapshot();
                if (tool === "pencil") paintCell(cell.x, cell.y);
                else eraseCell(cell.x, cell.y);
                commit();
                dragStateRef.current = {
                    kind: "freehand",
                    tool,
                    lastX: cell.x,
                    lastY: cell.y,
                };
                return;
            }

            if (tool === "rect" || tool === "ellipse" || tool === "line") {
                dragStateRef.current = {
                    kind: "shape",
                    tool,
                    startX: cell.x,
                    startY: cell.y,
                    curX: cell.x,
                    curY: cell.y,
                };
                renderOverlay();
            }
        },
        [
            tool,
            eventToCell,
            sampleColor,
            setColor,
            restoreLastPaintTool,
            snapshot,
            commit,
            fillAt,
            paintCell,
            eraseCell,
            renderOverlay,
        ],
    );

    const onPointerMove = useCallback(
        (e: React.PointerEvent<HTMLCanvasElement>) => {
            const cell = eventToCell(e);
            setHoverCell((prev) =>
                prev && prev.x === cell.x && prev.y === cell.y ? prev : cell,
            );

            const drag = dragStateRef.current;
            if (!drag) {
                renderOverlay();
                return;
            }

            if (drag.kind === "freehand") {
                if (drag.lastX === cell.x && drag.lastY === cell.y) return;
                if (drag.tool === "pencil") {
                    paintStroke(drag.lastX, drag.lastY, cell.x, cell.y);
                } else {
                    eraseStroke(drag.lastX, drag.lastY, cell.x, cell.y);
                }
                drag.lastX = cell.x;
                drag.lastY = cell.y;
                commit();
                return;
            }

            if (drag.kind === "shape") {
                if (drag.curX === cell.x && drag.curY === cell.y) return;
                drag.curX = cell.x;
                drag.curY = cell.y;
                renderOverlay();
            }
        },
        [eventToCell, paintStroke, eraseStroke, commit, renderOverlay],
    );

    const onPointerUp = useCallback(
        (e: React.PointerEvent<HTMLCanvasElement>) => {
            const cv = editorCanvasRef.current;
            if (cv) {
                try {
                    cv.releasePointerCapture(e.pointerId);
                } catch {
                    /* ignore */
                }
            }

            const drag = dragStateRef.current;
            dragStateRef.current = null;

            if (!drag) return;

            if (drag.kind === "shape") {
                snapshot();
                if (drag.tool === "rect") {
                    commitRect(drag.startX, drag.startY, drag.curX, drag.curY);
                } else if (drag.tool === "ellipse") {
                    commitEllipse(drag.startX, drag.startY, drag.curX, drag.curY);
                } else {
                    commitLine(drag.startX, drag.startY, drag.curX, drag.curY);
                }
                commit();
                renderOverlay();
            }
        },
        [
            snapshot,
            commit,
            commitRect,
            commitEllipse,
            commitLine,
            renderOverlay,
        ],
    );

    const onPointerLeave = useCallback(() => {
        setHoverCell(null);
    }, []);

    useUndoRedoKeyboard({ undo, redo });

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (
                target &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.isContentEditable)
            ) {
                return;
            }
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            const map: Record<string, ToolKind> = {
                b: "pencil",
                p: "pencil",
                e: "eraser",
                g: "bucket",
                r: "rect",
                o: "ellipse",
                l: "line",
                i: "eyedropper",
            };
            const next = map[e.key.toLowerCase()];
            if (next) {
                e.preventDefault();
                setTool(next);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [setTool]);

    const shouldAutoRenderOutput =
        showOutputPane &&
        (outputMode !== "raw" || filledCount <= RAW_OUTPUT_AUTO_LIMIT);

    const outputResult = useMemo(() => {
        if (!showOutputPane) return null;
        if (!shouldAutoRenderOutput) return null;
        return renderPixels(
            pixelsRef.current,
            width,
            height,
            outputMode,
            outputSize,
            outputSize,
            cellShape,
        );
        // pixelsVersion bumps when the buffer mutates
        // forceOutputRender bumps when user explicitly re-renders the heavy raw view
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showOutputPane,
        shouldAutoRenderOutput,
        outputMode,
        outputSize,
        cellShape,
        width,
        height,
        pixelsVersion,
        forceOutputRender,
    ]);

    const outputSvgString = useMemo(() => {
        if (!outputResult) return null;
        return buildSvgDocument(
            outputResult.body,
            outputSize,
            outputSize,
            cellShape,
        );
    }, [outputResult, outputSize, cellShape]);

    const outputSizeBytes = useMemo(() => {
        if (!outputSvgString) return 0;
        return new Blob([outputSvgString]).size;
    }, [outputSvgString]);

    const showCursorClass = (() => {
        switch (tool) {
            case "eyedropper":
                return "pixel-svg-canvas__editor-canvas--eyedrop";
            case "bucket":
                return "pixel-svg-canvas__editor-canvas--bucket";
            default:
                return "pixel-svg-canvas__editor-canvas--cross";
        }
    })();

    return (
        <div className="panel-viewer pixel-svg-canvas-viewer">
            <div className="svg-zoom-controls">
                <button
                    type="button"
                    className="zoom-button"
                    onClick={zoomOut}
                    title="Zoom out"
                >
                    −
                </button>
                <span className="zoom-level">
                    {Math.round(previewZoom * 100)}%
                </span>
                <button
                    type="button"
                    className="zoom-button"
                    onClick={zoomIn}
                    title="Zoom in"
                >
                    +
                </button>
                <button
                    type="button"
                    className="zoom-button"
                    onClick={resetPreviewZoom}
                    title="Reset zoom"
                >
                    ⌂
                </button>
            </div>
            <div className="svg-scroll-wrapper pixel-svg-canvas-viewer-scroll">
                <div
                    className={[
                        "pixel-svg-canvas-panes",
                        !showOutputPane ? "pixel-svg-canvas-panes--single" : "",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                >
                <div className="pixel-svg-canvas-pane">
                    <div className="pixel-svg-canvas-pane-header">
                        <h3>Editor ({width}x{height})</h3>
                        <span className="pixel-svg-canvas-stat">
                            {filledCount.toLocaleString()} filled cell
                            {filledCount === 1 ? "" : "s"}
                        </span>
                    </div>
                    <div
                        className={[
                            "pixel-svg-canvas-stage",
                            editorWhiteBackground
                                ? "pixel-svg-canvas-stage--editor-white"
                                : "",
                        ]
                            .filter(Boolean)
                            .join(" ")}
                        style={{ width: stageW, height: stageH }}
                    >
                        <canvas
                            ref={editorCanvasRef}
                            className={`pixel-svg-canvas__editor-canvas ${showCursorClass}`}
                            width={width}
                            height={height}
                            style={{ width: stageW, height: stageH }}
                            onPointerDown={onPointerDown}
                            onPointerMove={onPointerMove}
                            onPointerUp={onPointerUp}
                            onPointerCancel={onPointerUp}
                            onPointerLeave={onPointerLeave}
                        />
                        <canvas
                            ref={overlayCanvasRef}
                            className="pixel-svg-canvas__overlay-canvas"
                            width={width}
                            height={height}
                            style={{ width: stageW, height: stageH }}
                        />
                    </div>
                    <p className="pixel-svg-canvas-hint">
                        Tools: B pencil - E eraser - G bucket - R rect - O ellipse - L line - I eyedropper - Ctrl+Z / Ctrl+Y undo/redo
                    </p>
                </div>

                {showOutputPane && (
                    <div className="pixel-svg-canvas-pane">
                        <div className="pixel-svg-canvas-pane-header">
                            <h3>
                                Output SVG ({outputModeLabel(outputMode)}){" "}
                                {outputSize}x{outputSize}
                            </h3>
                            <button
                                type="button"
                                className="generic-button"
                                onClick={downloadSvg}
                                disabled={filledCount === 0}
                                title="Download the rendered SVG"
                            >
                                Download SVG
                            </button>
                        </div>
                        <div
                            className="pixel-svg-canvas-stage pixel-svg-canvas-stage--output"
                            style={{ width: stageW, height: stageH }}
                        >
                            {outputSvgString ? (
                                <div
                                    className="pixel-svg-canvas__output"
                                    style={{ width: stageW, height: stageH }}
                                    dangerouslySetInnerHTML={{ __html: outputSvgString }}
                                />
                            ) : (
                                <div className="pixel-svg-canvas-output-placeholder">
                                    <p>
                                        Raw mode would emit{" "}
                                        <strong>{filledCount.toLocaleString()}</strong>{" "}
                                        {rawModeElementPhrase(cellShape)} - too many to
                                        preview live.
                                    </p>
                                    <button
                                        type="button"
                                        className="generic-button"
                                        onClick={() => setForceOutputRender((v) => v + 1)}
                                    >
                                        Render preview anyway
                                    </button>
                                    <p className="pixel-svg-canvas-output-placeholder-hint">
                                        Tip: switch to <em>Greedy rects</em> or{" "}
                                        <em>Per-color path</em> for far smaller output.
                                    </p>
                                </div>
                            )}
                        </div>
                        {outputResult && (
                            <p className="pixel-svg-canvas-hint">
                                {outputResult.count.toLocaleString()}{" "}
                                {outputElementPhrase(
                                    outputMode,
                                    cellShape,
                                    outputResult.count,
                                )}{" "}
                                - {formatBytes(outputSizeBytes)}
                            </p>
                        )}
                    </div>
                )}
                </div>
            </div>
        </div>
    );
}

function rawModeElementPhrase(shape: CellShape): string {
    if (shape === "circle") {
        return "`<circle>` elements";
    }
    if (shape === "rounded") {
        return "`<rect rx/ry>` elements";
    }
    return "`<rect>` elements";
}

function outputElementPhrase(
    mode: OutputMode,
    shape: CellShape,
    count: number,
): string {
    const pl = count === 1 ? "" : "s";
    if (mode === "paths") return `path${pl}`;
    if (shape === "circle") return `circle${pl}`;
    if (shape === "rounded") {
        return count === 1 ? "rounded rect" : "rounded rects";
    }
    return `rect${pl}`;
}

function outputModeLabel(mode: string): string {
    switch (mode) {
        case "raw":
            return "raw rects";
        case "greedy-rects":
            return "greedy rects";
        case "paths":
            return "per-color path";
        default:
            return mode;
    }
}

function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
